import { z } from 'zod';
import { MoneySchema } from './common.js';
// Wire shape for Decimal columns — Prisma emits canonical strings; accept
// either string or non-negative number (matches AR / prepaid / accrual).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// ============================================================
// ReclassEntryStatus enum (mirrors Prisma)
// ============================================================
export const ReclassEntryStatusSchema = z.enum(['DRAFT', 'POSTED', 'REVERSED']);
// ============================================================
// Reclassification category codes — the 8 spec §36 categories. Validated as
// a string union on the API boundary; the prototype's category picker
// constrains the UX.
// ============================================================
export const ReclassCategorySchema = z.enum([
    'CURRENT_LT', // Current portion of LT Loan (TAS 1.69)
    'DOUBTFUL', // ลูกหนี้สงสัยจะสูญ (TAS 1.78)
    'HELD_SALE', // Asset Held for Sale (TFRS 5)
    'DEPOSIT_LT', // เงินมัดจำลูกค้า > 1 ปี (TAS 1.69)
    'INVEST_CAT', // เงินลงทุน — เปลี่ยน Category (TFRS 9)
    'PROVISION', // Provision Re-class (TAS 37)
    'REVENUE_TYPE', // Reclass ระหว่างประเภทรายได้ (TFRS 15)
    'OTHER', // อื่นๆ ตามที่ Auditor แนะนำ
]);
// ============================================================
// ReclassificationEntry — header row
// ============================================================
export const ReclassificationEntrySchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    code: z.string().min(1),
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    category: z.string().min(1),
    description: z.string().min(1),
    fromAccountCode: z.string().min(1),
    toAccountCode: z.string().min(1),
    amount: DecimalLike,
    status: ReclassEntryStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    reversalJournalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    postedAt: z.coerce.date().nullable().optional(),
    reversedBy: z.string().nullable().optional(),
    reversedAt: z.coerce.date().nullable().optional(),
    reverseAtNextPeriod: z.boolean(),
    createdBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row — derived display fields for the register table.
export const ReclassificationEntryListRowSchema = ReclassificationEntrySchema.extend({
    fromAccountName: z.string().nullable().optional(),
    toAccountName: z.string().nullable().optional(),
});
// ============================================================
// POST /reclass-entries — create a DRAFT row (no JV yet)
//
// fromAccountCode ≠ toAccountCode is enforced — reclassing into the same
// account is a no-op and almost always a user error.
// ============================================================
export const CreateReclassEntrySchema = z
    .object({
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
    category: ReclassCategorySchema,
    description: z.string().min(1),
    fromAccountCode: z.string().min(1),
    toAccountCode: z.string().min(1),
    amount: z.coerce.number().positive(),
    reverseAtNextPeriod: z.boolean().optional().default(true),
})
    .refine((v) => v.fromAccountCode !== v.toAccountCode, {
    message: 'fromAccountCode must differ from toAccountCode',
    path: ['toAccountCode'],
});
// ============================================================
// PATCH /reclass-entries/:id — DRAFT only
// ============================================================
export const UpdateReclassEntrySchema = z.object({
    period: z
        .string()
        .regex(/^\d{6}$/, 'period must be YYYYMM')
        .optional(),
    category: ReclassCategorySchema.optional(),
    description: z.string().min(1).optional(),
    fromAccountCode: z.string().min(1).optional(),
    toAccountCode: z.string().min(1).optional(),
    amount: z.coerce.number().positive().optional(),
    reverseAtNextPeriod: z.boolean().optional(),
});
// ============================================================
// POST /reclass-entries/reverse-next-period — batch reversal
// ============================================================
export const ReverseReclassNextPeriodSchema = z.object({
    // The ORIGINAL period whose POSTED entries should be reversed. The reversal
    // JV is dated to the first day of (period + 1).
    period: z.string().regex(/^\d{6}$/, 'period must be YYYYMM'),
});
export const ReverseReclassResultSchema = z.object({
    period: z.string(),
    // Period the reversal JVs were posted in (period + 1).
    reversalPeriod: z.string(),
    entriesReversed: z.number().int().nonnegative(),
    entriesSkipped: z.number().int().nonnegative(),
    totalAmount: DecimalLike,
    reversalJournalEntryIds: z.array(z.uuid()),
});
//# sourceMappingURL=reclass.js.map